export type LoadedSample = {
  id: string;
  name: string;
  url: string;
  buffer: AudioBuffer | null;
  isLoopCandidate: boolean;
  source: 'folder' | 'drop' | 'procedural' | 'chop';
  /** Relative path inside the loaded folder (`"drums/kick.wav"`) — used for the file browser. */
  relativePath?: string;
};

export type SampleManifest = {
  samples: Array<{ file: string; loop?: boolean; name?: string }>;
};

/** Timbre spec for synthesizeWav. Basic osc kinds + wavetable-style presets for bass/lead. */
export type SynthSpec =
  | { kind: 'sine' | 'triangle' | 'square' | 'sawtooth' }
  | { kind: 'noise' }
  | { kind: 'cowbell' }
  | { kind: 'wt-sub'; detune: number }
  | { kind: 'wt-reese'; detune: number }
  | { kind: 'wt-supersaw'; voices: number; detune: number }
  | { kind: 'wt-fm'; ratio: number; index: number }
  | { kind: 'wt-pwm'; width: number; lfoRate: number; lfoDepth: number }
  | { kind: 'wt-sync'; slaveMult: number }
  | { kind: 'wt-additive'; harmonics: number[] }
  | { kind: 'wt-bell'; modFreqMult: number; modDepth: number }
  | { kind: 'wt-formant'; vowel: 'a' | 'e' | 'i' | 'o' | 'u' };

const AUDIO_EXT = /\.(wav|mp3|ogg|flac|m4a|aac|webm)$/i;

function stripExtension(name: string): string {
  return name.replace(AUDIO_EXT, '');
}

function looksLikeLoop(name: string): boolean {
  return /loop|drone|pad|ambience|ambient|groove|beat|drum.?loop/i.test(name);
}

export type SoundMode = 'system' | 'folder' | 'sample';

export type ChopState = {
  buffer: AudioBuffer;
  slices: AudioBuffer[];
  /** Regions in source-order, tinted by the play-slot color they map to. */
  regions: Array<{ start: number; end: number; playIndex: number; color: string }>;
  /** Slice colors indexed by play order (chopSamples[i] uses sliceColors[i]). */
  sliceColors: string[];
  sourceName: string;
};

export class SampleLibrary {
  private samples: LoadedSample[] = [];
  private context: AudioContext | null = null;
  private listeners = new Set<() => void>();
  private mode: SoundMode = 'system';
  private chopSource: AudioBuffer | null = null;
  private chopSourceOriginal: AudioBuffer | null = null;
  private chopSourceName = '';
  /** NOIZ FX applied to the source before slicing. Each param 0..1. */
  private chopFx: { reverb: number; drive: number; down: number; ott: number; crunch: number } = {
    reverb: 0,
    drive: 0,
    down: 0,
    ott: 0,
    crunch: 0,
  };
  private lastRechopArgs: { count: number; mode: 'uniform' | 'random'; rng: () => number } | null =
    null;
  private chopSamples: LoadedSample[] = [];
  /** Regions in source-order — each has a `playIndex` (which slice slot this region provides audio for). */
  private chopRegions: Array<{ start: number; end: number; playIndex: number }> = [];
  /** Per-row sample overrides — click a row label + load a sound to pin it to that row. */
  private rowOverrides: Array<LoadedSample | null> = Array(6).fill(null);
  /** Which slice each of the 6 sequencer rows currently maps to. With N > 6
   *  slices loaded, we pick 6 unique random slots so the row labels show a
   *  varied cross-section of the sample instead of always slices 1–6.
   *  Reshuffled every time rechop() runs (via user action or reshuffle btn). */
  private rowSliceMap: number[] = [0, 1, 2, 3, 4, 5];
  /** Per-row "keep this slice pinned" flags. When true, that row's rowSliceMap
   *  entry survives reshuffles so the user can lock e.g. a kick sound in
   *  place while the other 5 rows cycle through the pool. */
  private rowLocks: boolean[] = [false, false, false, false, false, false];

  setContext(context: AudioContext): void {
    this.context = context;
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  getMode(): SoundMode {
    return this.mode;
  }

  setMode(mode: SoundMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.notify();
  }

  /** Single active sample — the one all bricks trigger. */
  getActiveSample(): LoadedSample | null {
    const pool = this.samples.filter((s) =>
      this.mode === 'system' ? s.source === 'procedural' : s.source !== 'procedural',
    );
    return pool[0] ?? null;
  }

  list(): LoadedSample[] {
    return this.samples.slice();
  }

  count(): number {
    return this.samples.length;
  }

  getByIndex(index: number): LoadedSample | null {
    if (this.samples.length === 0) return null;
    return this.samples[((index % this.samples.length) + this.samples.length) % this.samples.length] ?? null;
  }

  getById(id: string): LoadedSample | null {
    return this.samples.find((s) => s.id === id) ?? null;
  }

  async loadFromPublicFolder(): Promise<void> {
    try {
      const response = await fetch('/samples/manifest.json', { cache: 'no-cache' });
      if (!response.ok) return;
      const manifest = (await response.json()) as SampleManifest;
      for (const entry of manifest.samples ?? []) {
        const file = entry.file;
        if (!file) continue;
        const url = file.startsWith('/') ? file : `/samples/${file}`;
        const name = entry.name ?? stripExtension(file.split('/').pop() ?? file);
        await this.addFromUrl(url, name, entry.loop ?? looksLikeLoop(name), 'folder');
      }
    } catch {
      // No manifest is fine — user can drag-drop samples.
    }
  }

  async addFiles(files: FileList | File[]): Promise<number> {
    let added = 0;
    for (const file of Array.from(files)) {
      if (!AUDIO_EXT.test(file.name) && !file.type.startsWith('audio/')) continue;
      const url = URL.createObjectURL(file);
      const name = stripExtension(file.name);
      // webkitRelativePath is non-empty when the picker was `webkitdirectory` — used for the tree view.
      const rel =
        (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
      await this.addFromUrl(url, name, looksLikeLoop(name), 'drop', rel);
      added += 1;
    }
    return added;
  }

  /** Same as addFiles but takes explicit relative paths (drag-drop entries don't
   *  set webkitRelativePath, so we walk the DataTransfer tree ourselves and pass
   *  the constructed path in here). */
  async addDroppedTree(entries: Array<{ file: File; relativePath: string }>): Promise<number> {
    let added = 0;
    for (const { file, relativePath } of entries) {
      if (!AUDIO_EXT.test(file.name) && !file.type.startsWith('audio/')) continue;
      const url = URL.createObjectURL(file);
      const name = stripExtension(file.name);
      await this.addFromUrl(url, name, looksLikeLoop(name), 'drop', relativePath);
      added += 1;
    }
    return added;
  }

  /** Captured so we can regenerate procedurals from a Randomize click. */
  private decodeFn: ((data: ArrayBuffer) => Promise<AudioBuffer>) | null = null;

  /** Per-slot name pools — Randomize picks one from each bank so slot 0 might
   *  be `kick` on one roll and `boom` on the next. Row labels + the Default
   *  panel list read from the current picks via getSystemRowNames(). */
  private static readonly SLOT_NAME_BANKS: readonly (readonly string[])[] = [
    ['kick', 'boom', 'thud', 'punch'],
    ['snare', 'clap', 'rim', 'snap'],
    ['hat', 'tick', 'shaker', 'ping'],
    ['perc', 'cow', 'wood', 'ride'],
    ['bass', 'sub', 'reese', 'mono'],
    ['lead', 'pluck', 'stab', 'bell'],
  ];

  /** Current name picked for each of the 6 procedural slots. */
  private procSlotNames: string[] = ['kick', 'snare', 'hat', 'perc', 'bass', 'lead'];

  getSystemRowNames(): readonly string[] {
    return this.procSlotNames;
  }

  async ensureProceduralFallback(decode: (data: ArrayBuffer) => Promise<AudioBuffer>): Promise<void> {
    this.decodeFn = decode;
    if (this.samples.length > 0) return;

    // Six sounds — one per brick row. Drums up top (one-shots), tonal at the bottom (loopable sustains).
    const specs: Array<{
      name: string;
      freq: number;
      duration: number;
      spec: SynthSpec;
      loop: boolean;
    }> = [
      { name: 'kick', freq: 80, duration: 0.24, spec: { kind: 'sine' }, loop: false },
      { name: 'snare', freq: 200, duration: 0.18, spec: { kind: 'noise' }, loop: false },
      { name: 'hat', freq: 6400, duration: 0.05, spec: { kind: 'square' }, loop: false },
      { name: 'perc', freq: 800, duration: 0.22, spec: { kind: 'cowbell' }, loop: false },
      { name: 'bass', freq: 55, duration: 1.0, spec: { kind: 'wt-sub', detune: 8 }, loop: true },
      { name: 'lead', freq: 220, duration: 1.0, spec: { kind: 'wt-supersaw', voices: 5, detune: 12 }, loop: true },
    ];

    for (let i = 0; i < specs.length; i++) {
      const s = specs[i]!;
      const wav = this.synthesizeWav(s.freq, s.duration, s.spec, s.loop);
      const buffer = await decode(wav);
      // IDs are stable per slot (proc-slot-N) so bricks + sequencer steps
      // keep their references across Randomize even if the name changes.
      this.samples.push({
        id: `proc-slot-${i}`,
        name: s.name,
        url: '',
        buffer,
        isLoopCandidate: s.loop,
        source: 'procedural',
      });
      this.procSlotNames[i] = s.name;
    }
    this.notify();
  }

  /** Regenerate the six procedural drum/synth samples with randomized params. */
  async randomizeSystemSounds(rng: () => number): Promise<void> {
    if (!this.decodeFn) return;
    const pick = <T>(arr: readonly T[]): T => arr[Math.floor(rng() * arr.length)]!;
    // 7 wavetable-style timbres — used for bass + lead so those rows have real
    // synth variety instead of just "saw or square".
    const bassPresets: SynthSpec[] = [
      { kind: 'sawtooth' },
      { kind: 'square' },
      { kind: 'triangle' },
      { kind: 'wt-sub', detune: 4 + rng() * 12 },
      { kind: 'wt-reese', detune: 8 + rng() * 22 },
      { kind: 'wt-fm', ratio: pick([0.5, 1, 2]), index: 1 + rng() * 4 },
      { kind: 'wt-pwm', width: 0.3 + rng() * 0.35, lfoRate: 0.5 + rng() * 2.5, lfoDepth: 0.15 + rng() * 0.25 },
      { kind: 'wt-sync', slaveMult: 1.5 + rng() * 3 },
      { kind: 'wt-additive', harmonics: [1, 0, 0.6 * rng(), 0, 0.35 * rng(), 0, 0.2 * rng()] },
    ];
    const leadPresets: SynthSpec[] = [
      { kind: 'sawtooth' },
      { kind: 'square' },
      { kind: 'triangle' },
      { kind: 'wt-supersaw', voices: 5, detune: 6 + rng() * 18 },
      { kind: 'wt-fm', ratio: pick([2, 3, 4]), index: 2 + rng() * 6 },
      { kind: 'wt-pwm', width: 0.35 + rng() * 0.3, lfoRate: 2 + rng() * 6, lfoDepth: 0.25 + rng() * 0.35 },
      { kind: 'wt-sync', slaveMult: 2 + rng() * 5 },
      { kind: 'wt-additive', harmonics: [1, 0.5 * rng(), 0.4 * rng(), 0.3 * rng(), 0.25 * rng(), 0.2 * rng(), 0.15 * rng()] },
      { kind: 'wt-bell', modFreqMult: pick([1.4, 2.1, 3.3, 4.7]), modDepth: 0.3 + rng() * 0.5 },
      { kind: 'wt-formant', vowel: pick(['a', 'e', 'i', 'o', 'u']) },
    ];
    // Slot 0-3 = percussive (freqs vary widely per pick), 4-5 = tonal loops.
    // The name assigned to each slot is picked from SLOT_NAME_BANKS so the
    // Default panel + row labels rotate through fresh names on each roll.
    const banks = SampleLibrary.SLOT_NAME_BANKS;
    const newNames = banks.map((b) => pick(b));
    // Shared musical root for the bass + lead pair. Both loops derive their
    // base freq from this so every brick — regardless of which row it arms —
    // plays notes in the same key. Combined with the MAJOR_PENTATONIC rate
    // pool in Game.ts, all triggered notes belong to one major pentatonic
    // scale rooted here.
    const rootFreq = 22 + rng() * 20; // ~22-42 Hz (F0..E1) — deep sub territory
    // Lead sits 2 or 3 octaves above the bass so it lands in the melody
    // register but shares the exact pitch class.
    const leadOctaves = rng() > 0.5 ? 4 : 8;
    // Percussive preset banks — each slot picks a random voicing AND a random
    // freq/duration from a wider range, so kick/snare/hat/perc feel different
    // across sessions instead of always the same sine/noise/square/cowbell.
    // Pool blends waveform slots (sine/triangle/square/noise/cowbell) with a
    // few wavetable presets so occasional rerolls get metallic or FM-y hits.
    // Kick — sine/triangle bodies with a rare wt-sub for extra weight. FM
    // stays out (too clangy at kick pitches). Sine/triangle weighted heavy so
    // most rolls stay in classic kick territory; wt-sub included at low
    // detune only, so it reads as "heavy kick" instead of sustained bass.
    const kickPresets: SynthSpec[] = [
      { kind: 'sine' },
      { kind: 'sine' },
      { kind: 'triangle' },
      { kind: 'wt-sub', detune: 1 + rng() * 3 },
    ];
    const snarePresets: SynthSpec[] = [
      { kind: 'noise' },
      { kind: 'noise' }, // weight noise higher — still reads as a snare most of the time
      { kind: 'square' },
      { kind: 'sawtooth' },
      { kind: 'triangle' },
      { kind: 'wt-fm', ratio: pick([1.4, 2, 3.1]), index: 3 + rng() * 5 },
      { kind: 'wt-sync', slaveMult: 2 + rng() * 4 },
    ];
    const hatPresets: SynthSpec[] = [
      { kind: 'square' },
      { kind: 'noise' },
      { kind: 'sawtooth' },
      { kind: 'triangle' },
      { kind: 'wt-fm', ratio: pick([3.7, 5.4, 7.1]), index: 1 + rng() * 3 },
      { kind: 'wt-bell', modFreqMult: pick([2.1, 3.3, 4.7]), modDepth: 0.2 + rng() * 0.4 },
    ];
    const percPresets: SynthSpec[] = [
      { kind: 'cowbell' },
      { kind: 'triangle' },
      { kind: 'square' },
      { kind: 'sawtooth' },
      { kind: 'sine' },
      { kind: 'wt-bell', modFreqMult: pick([1.4, 2.1, 3.3]), modDepth: 0.3 + rng() * 0.5 },
      { kind: 'wt-fm', ratio: pick([1.5, 2.5, 4]), index: 2 + rng() * 4 },
    ];
    const specs: Array<{
      freq: number;
      duration: number;
      spec: SynthSpec;
      loop: boolean;
    }> = [
      // Kick: 40-140 Hz for pitch variety (deep sub → thumpy attack). Duration
      // capped at ~0.22s so envelopes stay percussive — longer sustains start
      // sounding like a bass note, and this slot should always read as a hit.
      { freq: 40 + rng() * 100,   duration: 0.12 + rng() * 0.10, spec: pick(kickPresets),  loop: false },
      // Snare: 200-800 Hz keeps rolls in tap / crack territory. Earlier
      // floor (120 Hz) was landing sub-rims that read as low toms; lifting
      // the min plus a tighter max duration keeps the slot snappy.
      { freq: 200 + rng() * 600,  duration: 0.06 + rng() * 0.14, spec: pick(snarePresets), loop: false },
      // Hat: freq 3-12 kHz for classic ticks up to shimmery hats.
      { freq: 3000 + rng() * 9000,duration: 0.03 + rng() * 0.10, spec: pick(hatPresets),   loop: false },
      // Perc: freq 300-2000 Hz — cowbell / bell / metallic pop range.
      { freq: 300 + rng() * 1700, duration: 0.08 + rng() * 0.28, spec: pick(percPresets),  loop: false },
      { freq: rootFreq,                duration: 0.8 + rng() * 0.6, spec: pick(bassPresets), loop: true },
      { freq: rootFreq * leadOctaves,  duration: 0.8 + rng() * 0.6, spec: pick(leadPresets), loop: true },
    ];

    for (let slot = 0; slot < specs.length; slot++) {
      const s = specs[slot]!;
      const wav = this.synthesizeWav(s.freq, s.duration, s.spec, s.loop);
      const buffer = await this.decodeFn(wav);
      const newName = newNames[slot]!;
      const slotId = `proc-slot-${slot}`;
      const existing = this.samples.find((x) => x.id === slotId);
      if (existing) {
        existing.buffer = buffer;
        existing.isLoopCandidate = s.loop;
        existing.name = newName;
      } else {
        this.samples.push({
          id: slotId,
          name: newName,
          url: '',
          buffer,
          isLoopCandidate: s.loop,
          source: 'procedural',
        });
      }
      this.procSlotNames[slot] = newName;
    }
    this.notify();
  }

  /** Look up a sample by exact name (used for row → instrument assignment). */
  getByName(name: string): LoadedSample | null {
    return this.samples.find((s) => s.name === name) ?? null;
  }

  /** Sample for a given brick row. Slot override (if loaded) wins in every mode. */
  getSampleForRow(row: number, rowSampleNames: readonly string[]): LoadedSample | null {
    const override = this.rowOverrides[row];
    if (override) return override;
    if (this.mode === 'folder') {
      const folder = this.samples.filter((s) => s.source === 'folder' || s.source === 'drop');
      if (folder.length === 0) return null;
      return folder[row % folder.length] ?? null;
    }
    if (this.mode === 'sample') {
      if (this.chopSamples.length === 0) return null;
      const idx = this.rowSliceMap[row % 6] ?? row;
      return this.chopSamples[idx % this.chopSamples.length] ?? null;
    }
    const wanted = rowSampleNames[row % rowSampleNames.length];
    if (!wanted) return null;
    return this.getByName(wanted);
  }

  // ---- Row slot overrides ----

  getRowOverride(row: number): LoadedSample | null {
    return this.rowOverrides[row] ?? null;
  }

  async loadRowOverride(row: number, file: File): Promise<boolean> {
    if (!this.context) return false;
    try {
      const data = await file.arrayBuffer();
      const buffer = await this.context.decodeAudioData(data.slice(0));
      SampleLibrary.normalizeBuffer(buffer);
      this.rowOverrides[row] = {
        id: `slot-${row}-${file.name}-${Date.now()}`,
        name: stripExtension(file.name),
        url: '',
        buffer,
        isLoopCandidate: false,
        source: 'drop',
      };
      this.notify();
      return true;
    } catch (error) {
      console.warn(`Failed to load row ${row} override`, error);
      return false;
    }
  }

  clearRowOverride(row: number): void {
    if (!this.rowOverrides[row]) return;
    this.rowOverrides[row] = null;
    this.notify();
  }

  /** Pin an already-loaded sample (folder / drop / chop) to a row slot. */
  setRowOverrideFromSample(row: number, sampleId: string): boolean {
    const sample = this.samples.find((s) => s.id === sampleId)
      ?? this.chopSamples.find((s) => s.id === sampleId);
    if (!sample) return false;
    this.rowOverrides[row] = sample;
    this.notify();
    return true;
  }

  /** Pin the full uncut chop source (whole loaded sample) to a row slot. Used
   *  when the user drags the sample-panel waveform onto a sequencer row. */
  setRowOverrideFromChopSource(row: number): boolean {
    if (!this.chopSourceOriginal) return false;
    this.rowOverrides[row] = {
      id: `chop-source-${row}-${Date.now()}`,
      name: this.chopSourceName || 'sample',
      url: '',
      buffer: this.chopSourceOriginal,
      isLoopCandidate: false,
      source: 'drop',
    };
    this.notify();
    return true;
  }

  /** Random sample from the current mode's pool. Folder + Sample modes use this so
   *  every brick gets a different slice/file instead of just the first 6. */
  getRandomSample(rng: () => number): LoadedSample | null {
    if (this.mode === 'sample') {
      if (this.chopSamples.length === 0) return null;
      return this.chopSamples[Math.floor(rng() * this.chopSamples.length)] ?? null;
    }
    const pool = this.samples.filter((s) =>
      this.mode === 'system'
        ? s.source === 'procedural'
        : s.source === 'folder' || s.source === 'drop',
    );
    if (pool.length === 0) return null;
    return pool[Math.floor(rng() * pool.length)] ?? null;
  }

  // ---- Sample (chop) mode ----

  /** Load a single audio file as the chop source; slices are (re)generated afterwards. */
  async loadChopSource(file: File, sliceCount: number, rng: () => number): Promise<boolean> {
    if (!this.context) return false;
    try {
      const data = await file.arrayBuffer();
      const buffer = await this.context.decodeAudioData(data.slice(0));
      SampleLibrary.normalizeBuffer(buffer);
      this.chopSourceOriginal = buffer;
      this.chopSource = this.applyChopFx(buffer);
      this.chopSourceName = stripExtension(file.name);
      this.rechop(sliceCount, rng);
      this.notify();
      return true;
    } catch (error) {
      console.warn('Failed to load chop source', error);
      return false;
    }
  }

  /** Update NOIZ FX params, reprocess the source, and re-slice with the same
   *  count/mode as before. */
  setChopFx(
    params: Partial<{ reverb: number; drive: number; down: number; ott: number; crunch: number }>,
  ): void {
    Object.assign(this.chopFx, params);
    if (!this.chopSourceOriginal) return;
    this.chopSource = this.applyChopFx(this.chopSourceOriginal);
    if (this.lastRechopArgs) {
      const { count, rng, mode } = this.lastRechopArgs;
      this.rechop(count, rng, mode);
    } else {
      this.notify();
    }
  }

  getChopFx(): { reverb: number; drive: number; down: number; ott: number; crunch: number } {
    return { ...this.chopFx };
  }

  /**
   * Offline FX chain applied to the whole source before slicing:
   *   Down → Drive → OTT (3-band up/down comp) → Crunch → Reverb (last)
   * All params 0..1. Skipped entirely when all params are 0.
   */
  private applyChopFx(src: AudioBuffer): AudioBuffer {
    const { reverb, drive, down, ott, crunch } = this.chopFx;
    if (reverb === 0 && drive === 0 && down === 0 && ott === 0 && crunch === 0) return src;
    if (!this.context) return src;

    const out = this.context.createBuffer(src.numberOfChannels, src.length, src.sampleRate);
    // Sample-and-hold step for Down.
    const holdStep = Math.max(1, Math.round(1 + down * 24));
    const driveGain = 1 + drive * 8;
    const crunchThresh = 1 - crunch * 0.85;

    // OTT: Xfer-style 3-band split w/ parallel upward + downward compression per band.
    // Crossovers: ~250 Hz, ~2.5 kHz. Linkwitz-Riley 2nd-order (biquad LP + HP twice).
    // Each band compressed with a soft-knee downward comp AND a "reverse" upward comp
    // that pulls quiet material toward the threshold — the classic OTT sound.
    // 'ott' scales depth (0..1) and makeup.
    const sr = src.sampleRate;
    const lowCross = 250;
    const highCross = 2500;
    const makeLR2 = (cutoff: number, type: 'lp' | 'hp') => {
      const w = (2 * Math.PI * cutoff) / sr;
      const cosw = Math.cos(w);
      const sinw = Math.sin(w);
      const q = Math.SQRT1_2; // Butterworth
      const alpha = sinw / (2 * q);
      let b0: number, b1: number, b2: number;
      const a0 = 1 + alpha;
      const a1 = -2 * cosw;
      const a2 = 1 - alpha;
      if (type === 'lp') {
        b0 = (1 - cosw) / 2;
        b1 = 1 - cosw;
        b2 = (1 - cosw) / 2;
      } else {
        b0 = (1 + cosw) / 2;
        b1 = -(1 + cosw);
        b2 = (1 + cosw) / 2;
      }
      return { b0: b0 / a0, b1: b1 / a0, b2: b2 / a0, a1: a1 / a0, a2: a2 / a0 };
    };
    // Compressor state per band (env follower + gains).
    const attackMs = 5;
    const releaseMs = 80;
    const attackCoef = Math.exp(-1 / ((attackMs / 1000) * sr));
    const releaseCoef = Math.exp(-1 / ((releaseMs / 1000) * sr));
    const downThreshDb = -18 + (1 - ott) * 10; // deeper thresh at higher ott
    const downRatio = 3 + ott * 5; // 3:1 → 8:1
    const upThreshDb = -35 + (1 - ott) * 8;
    const upRatio = 2 + ott * 3;
    const makeupDb = ott * 6;
    const dbToGain = (db: number) => Math.pow(10, db / 20);
    const gainToDb = (g: number) => 20 * Math.log10(Math.max(1e-8, g));
    const makeupGain = dbToGain(makeupDb);

    // Reverb is a small Schroeder: 4 parallel combs → 2 series allpasses.
    // Delay lengths chosen coprime-ish; scaled by sample rate so 44.1k and 48k both feel right.
    const combLens = [1687, 1601, 2053, 2251].map((n) => Math.max(1, Math.round((n * sr) / 44100)));
    const apLens = [389, 307].map((n) => Math.max(1, Math.round((n * sr) / 44100)));
    const combFb = 0.7 + reverb * 0.22; // 0.7 → 0.92
    const apFb = 0.7;
    const wet = reverb;
    const dry = 1 - reverb * 0.5;

    // Per-band LR2 = 2× biquad in cascade → 4th-order LR.
    const lowLp1 = makeLR2(lowCross, 'lp');
    const lowLp2 = makeLR2(lowCross, 'lp');
    const midHp1 = makeLR2(lowCross, 'hp');
    const midHp2 = makeLR2(lowCross, 'hp');
    const midLp1 = makeLR2(highCross, 'lp');
    const midLp2 = makeLR2(highCross, 'lp');
    const highHp1 = makeLR2(highCross, 'hp');
    const highHp2 = makeLR2(highCross, 'hp');

    for (let ch = 0; ch < src.numberOfChannels; ch++) {
      const input = src.getChannelData(ch);
      const output = out.getChannelData(ch);
      const combBufs = combLens.map((n) => new Float32Array(n));
      const combIdx = new Uint32Array(combLens.length);
      const apBufs = apLens.map((n) => new Float32Array(n));
      const apIdx = new Uint32Array(apLens.length);
      // Biquad states (z1, z2) per filter.
      const bqState = () => ({ z1: 0, z2: 0 });
      const st = {
        lowLp1: bqState(), lowLp2: bqState(),
        midHp1: bqState(), midHp2: bqState(), midLp1: bqState(), midLp2: bqState(),
        highHp1: bqState(), highHp2: bqState(),
      };
      // Per-band envelope followers (peak, smoothed).
      let envLow = 0, envMid = 0, envHigh = 0;
      const biquad = (x: number, c: typeof lowLp1, s: { z1: number; z2: number }): number => {
        const y = c.b0 * x + s.z1;
        s.z1 = c.b1 * x - c.a1 * y + s.z2;
        s.z2 = c.b2 * x - c.a2 * y;
        return y;
      };
      const compressBand = (
        x: number,
        env: number,
      ): { out: number; env: number } => {
        const abs = Math.abs(x);
        const target = abs;
        const coef = target > env ? attackCoef : releaseCoef;
        const nextEnv = target + coef * (env - target);
        const envDb = gainToDb(nextEnv);
        // Downward comp (above thresh, reduce)
        let gainDb = 0;
        if (envDb > downThreshDb) {
          gainDb -= (envDb - downThreshDb) * (1 - 1 / downRatio);
        }
        // Upward comp (below thresh, boost)
        if (envDb < upThreshDb) {
          gainDb += (upThreshDb - envDb) * (1 - 1 / upRatio);
        }
        return { out: x * dbToGain(gainDb), env: nextEnv };
      };
      let held = 0;
      for (let i = 0; i < input.length; i++) {
        let s = input[i] ?? 0;
        if (down > 0 && i % holdStep !== 0) s = held;
        else held = s;
        if (drive > 0) s = Math.tanh(s * driveGain);
        if (ott > 0) {
          // Split into 3 bands (LR4 via cascaded LR2 biquads).
          const low = biquad(biquad(s, lowLp1, st.lowLp1), lowLp2, st.lowLp2);
          const midHi = biquad(biquad(s, midHp1, st.midHp1), midHp2, st.midHp2);
          const mid = biquad(biquad(midHi, midLp1, st.midLp1), midLp2, st.midLp2);
          const high = biquad(biquad(s, highHp1, st.highHp1), highHp2, st.highHp2);
          const lowC = compressBand(low, envLow); envLow = lowC.env;
          const midC = compressBand(mid, envMid); envMid = midC.env;
          const highC = compressBand(high, envHigh); envHigh = highC.env;
          // Sum + makeup, blend dry/wet by ott amount.
          const wet = (lowC.out + midC.out + highC.out) * makeupGain;
          s = s * (1 - ott) + wet * ott;
        }
        if (crunch > 0) {
          if (s > crunchThresh) s = crunchThresh;
          else if (s < -crunchThresh) s = -crunchThresh;
          s *= 1 + crunch * 0.6;
        }
        if (reverb > 0) {
          let combSum = 0;
          for (let c = 0; c < combLens.length; c++) {
            const buf = combBufs[c]!;
            const idx = combIdx[c]!;
            const delayed = buf[idx]!;
            buf[idx] = s + delayed * combFb;
            combIdx[c] = (idx + 1) % combLens[c]!;
            combSum += delayed;
          }
          let wetSig = combSum * 0.25;
          for (let a = 0; a < apLens.length; a++) {
            const buf = apBufs[a]!;
            const idx = apIdx[a]!;
            const delayed = buf[idx]!;
            const inSig = wetSig;
            buf[idx] = inSig + delayed * apFb;
            wetSig = delayed - inSig * apFb;
            apIdx[a] = (idx + 1) % apLens[a]!;
          }
          s = s * dry + wetSig * wet;
        }
        if (s > 1) s = 1;
        else if (s < -1) s = -1;
        output[i] = s;
      }
    }
    return out;
  }

  /**
   * Re-slice the current chop source into `count` evenly-spaced segments.
   * `mode` selects the slice order:
   *   - 'uniform' → slices play back in source order
   *   - 'random'  → slice buffers are shuffled so the same visual chops play
   *                 in a random order (Fisher-Yates)
   */
  rechop(count: number, rng: () => number, mode: 'uniform' | 'random' = 'uniform'): void {
    if (!this.chopSource) return;
    this.lastRechopArgs = { count, mode, rng };
    // 0 = loaded but not yet chopped — the canvas shows the plain waveform
    // and the user drags Slices up from zero to add chops. Max 64.
    const requested = Math.max(0, Math.min(64, Math.floor(count)));
    if (requested === 0) {
      this.chopRegions = [];
      this.chopSamples = [];
      this.shuffleRowSliceMap(rng);
      this.notify();
      return;
    }
    const total = this.chopSource.duration;
    // Short samples (a single hi-hat, one drum hit) can't sustain the
    // requested slice count without every slice falling under the 40ms
    // minimum and being dropped. Back off the slice count so each slice
    // clears the threshold — a hi-hat loaded whole is better than 0 slices.
    const MIN_SLICE_SEC = 0.04;
    const maxByDuration = Math.max(1, Math.floor(total / MIN_SLICE_SEC));
    const clampedCount = Math.min(requested, maxByDuration);
    // Random in/out points across the source — slices don't need to cover
    // the whole sample, and CAN overlap in the source domain (playing a
    // slice from 0.4-0.9s and another from 0.7-1.2s is fine, they're
    // separate buffers). Each slice picks a random start anywhere in the
    // source and a random duration up to MAX_CHOP_SECONDS. Per-row playback
    // holds (in the sequencer) then decide how much of each slice actually
    // gets heard, so length variety comes from the row category, not the
    // buffer size itself.
    const MAX_SLICE_SEC = 3;
    const rangesSec: Array<{ start: number; end: number }> = [];
    for (let i = 0; i < clampedCount; i++) {
      const start = rng() * Math.max(0, total - MIN_SLICE_SEC);
      const remaining = total - start;
      const desiredDur = 0.5 + rng() * (MAX_SLICE_SEC - 0.5);
      const dur = Math.min(desiredDur, remaining);
      if (dur < MIN_SLICE_SEC) continue;
      rangesSec.push({ start, end: start + dur });
    }
    // Compute play-order assignment. In uniform mode: region i → slice slot i.
    // In random mode: Fisher-Yates so slice slot 0 comes from a random region.
    const n = rangesSec.length;
    const playIndex = Array.from({ length: n }, (_, i) => i);
    if (mode === 'random') {
      for (let i = playIndex.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [playIndex[i], playIndex[j]] = [playIndex[j]!, playIndex[i]!];
      }
    }
    this.chopRegions = rangesSec.map((r, i) => ({
      start: r.start / total,
      end: r.end / total,
      playIndex: playIndex[i]!,
    }));
    // Build chopSamples indexed by slice slot (audio for slot k comes from region
    // where playIndex[i] === k).
    const samples: LoadedSample[] = new Array(n);
    for (let i = 0; i < n; i++) {
      const slot = playIndex[i]!;
      const r = rangesSec[i]!;
      samples[slot] = this.buildChopSample(slot, r.start, r.end);
    }
    this.chopSamples = samples.filter(Boolean);
    // Repopulate the row → slice map so the 6 sequencer rows sample a fresh
    // spread of the chop pool. If the pool has ≥ 6 slices, pick 6 unique
    // indices; otherwise fall back to modulo so every row still gets a hit.
    this.shuffleRowSliceMap(rng);
    this.notify();
  }

  /** Public wrapper — Game calls this on measure boundaries to rotate the
   *  6 visible slot labels through different slices without triggering a
   *  full rechop. No-op in non-sample modes. */
  reshuffleRowSliceMap(rng: () => number): void {
    if (this.mode !== 'sample' || this.chopSamples.length === 0) return;
    this.shuffleRowSliceMap(rng);
    this.notify();
  }

  /** Pick 6 unique random slice indices for the row → slice map. Called from
   *  rechop() so both initial chop and reshuffle-slices refresh the mapping.
   *  Locked rows keep their current slice; only unlocked rows get repicked
   *  from what's left of the pool. */
  private shuffleRowSliceMap(rng: () => number): void {
    const n = this.chopSamples.length;
    if (n === 0) {
      this.rowSliceMap = [0, 1, 2, 3, 4, 5];
      return;
    }
    const next = this.rowSliceMap.slice(0, 6);
    while (next.length < 6) next.push(next.length);
    // Slices already claimed by locked rows are off-limits so the unlocked
    // rows still get unique picks — losing dupes would otherwise put e.g. two
    // rows on slice 3 the moment lock-heavy sessions run out of headroom.
    const claimed = new Set<number>();
    for (let r = 0; r < 6; r++) {
      if (this.rowLocks[r]) claimed.add(next[r] ?? r);
    }
    const available = Array.from({ length: n }, (_, i) => i)
      .filter((i) => !claimed.has(i));
    // Fisher-Yates the available pool.
    for (let i = available.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [available[i], available[j]] = [available[j]!, available[i]!];
    }
    let cursor = 0;
    for (let r = 0; r < 6; r++) {
      if (this.rowLocks[r]) continue;
      if (cursor < available.length) {
        next[r] = available[cursor++]!;
      } else {
        // Pool smaller than the number of unlocked rows — fall back to a
        // random pick (may repeat) so every row still points somewhere valid.
        next[r] = Math.floor(rng() * n);
      }
    }
    this.rowSliceMap = next;
  }

  /** Toggle whether a row keeps its current slice across reshuffles. Returns
   *  the new locked state so the caller can update the UI without re-querying. */
  toggleRowLock(row: number): boolean {
    const idx = row % 6;
    this.rowLocks[idx] = !this.rowLocks[idx];
    this.notify();
    return this.rowLocks[idx]!;
  }

  isRowLocked(row: number): boolean {
    return !!this.rowLocks[row % 6];
  }

  /** Slice index currently assigned to a given sequencer row (0-based). Used
   *  by the HUD to render "17", "3", "12", ... labels + matching slice colors. */
  getRowSliceIndex(row: number): number {
    return this.rowSliceMap[row % 6] ?? row;
  }

  private static sliceColor(playIndex: number, total: number): string {
    // Evenly-spaced hues across the color wheel. High saturation, mid-bright so they
    // punch through the dark waveform / overlays without overpowering.
    const hue = Math.round((playIndex / Math.max(1, total)) * 360);
    return `hsl(${hue} 80% 60%)`;
  }

  private buildChopSample(idx: number, startSec: number, endSec: number): LoadedSample {
    const src = this.chopSource!;
    const sr = src.sampleRate;
    // Slice buffer holds the full region duration up to a 3s ceiling. The
    // sequencer applies PER-ROW holds on top of this so tracks act as length
    // categories: rows 0-2 = short perc, 3-4 = medium, 5 = long / full slice.
    // This avoids mud from multiple rows all playing long slices at once.
    const MAX_CHOP_SECONDS = 3;
    const cappedEnd = Math.min(endSec, startSec + MAX_CHOP_SECONDS);
    const startFrame = Math.floor(startSec * sr);
    const endFrame = Math.min(src.length, Math.floor(cappedEnd * sr));
    const frames = Math.max(1, endFrame - startFrame);
    const ctx = this.context!;
    const buffer = ctx.createBuffer(src.numberOfChannels, frames, sr);
    for (let ch = 0; ch < src.numberOfChannels; ch++) {
      const source = src.getChannelData(ch);
      const dest = buffer.getChannelData(ch);
      for (let i = 0; i < frames; i++) dest[i] = source[startFrame + i] ?? 0;
      // Short attack, quick tail fade so the chop reads as a percussive hit.
      const inFrames = Math.min(frames, Math.floor(sr * 0.002));
      const outFrames = Math.min(frames, Math.floor(sr * 0.02));
      for (let i = 0; i < inFrames; i++) dest[i]! *= i / inFrames;
      for (let i = 0; i < outFrames; i++) {
        const f = i / outFrames;
        dest[frames - 1 - i]! *= f;
      }
    }
    // Short source stub for the slice name so the row label reads e.g.
    // `dawn_slice01` after Import slices instead of the generic `slice 1`.
    const short = stripExtension(this.chopSourceName || 'sample')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '')
      .slice(0, 8) || 'chop';
    return {
      id: `chop-${this.chopSourceName}-${idx}`,
      name: `${short}_slice${String(idx + 1).padStart(2, '0')}`,
      url: '',
      buffer,
      isLoopCandidate: false,
      source: 'chop',
    };
  }

  getChopState(): ChopState | null {
    if (!this.chopSource) return null;
    const total = this.chopRegions.length;
    const sliceColors = Array.from({ length: total }, (_, k) =>
      SampleLibrary.sliceColor(k, total),
    );
    return {
      buffer: this.chopSource,
      slices: this.chopSamples
        .map((s) => s.buffer)
        .filter((b): b is AudioBuffer => b !== null),
      regions: this.chopRegions.map((r) => ({
        start: r.start,
        end: r.end,
        playIndex: r.playIndex,
        color: SampleLibrary.sliceColor(r.playIndex, total),
      })),
      sliceColors,
      sourceName: this.chopSourceName,
    };
  }

  /** Color for a specific slice slot (used by the sequencer row labels in sample mode). */
  getSliceColor(playIndex: number): string {
    return SampleLibrary.sliceColor(playIndex, Math.max(1, this.chopRegions.length));
  }

  private async addFromUrl(
    url: string,
    name: string,
    isLoopCandidate: boolean,
    source: LoadedSample['source'],
    relativePath?: string,
  ): Promise<void> {
    const id = `${source}-${name}-${this.samples.length}`;
    let buffer: AudioBuffer | null = null;
    if (this.context) {
      try {
        const response = await fetch(url);
        const data = await response.arrayBuffer();
        buffer = await this.context.decodeAudioData(data.slice(0));
        if (buffer) SampleLibrary.normalizeBuffer(buffer);
      } catch (error) {
        console.warn(`Failed to decode sample "${name}"`, error);
      }
    }

    // Replace same-named drop/folder samples.
    const existing = this.samples.findIndex((s) => s.name === name && s.source === source);
    const entry: LoadedSample = { id, name, url, buffer, isLoopCandidate, source, relativePath };
    if (existing >= 0) this.samples[existing] = entry;
    else this.samples.push(entry);
    this.notify();
  }

  /** Pin the current chop slices onto sequencer rows in order — row 0 → slice 0,
   *  row 1 → slice 1, wrapping if there are fewer slices than rows. This is the
   *  fast "commit these slices to the sequencer" gesture from the Sample panel;
   *  slices land as row overrides so they show in row labels and stay put
   *  through reshuffles. Returns the number of rows assigned. */
  importChopSlicesToSequencer(): number {
    if (this.chopSamples.length === 0) return 0;
    const rows = this.rowOverrides.length;
    let assigned = 0;
    for (let row = 0; row < rows; row++) {
      const slice = this.chopSamples[row];
      // Rows beyond the slice count get cleared so a smaller re-import
      // doesn't leave stale overrides in the sequencer from a bigger one.
      if (slice) {
        this.rowOverrides[row] = slice;
        assigned += 1;
      } else {
        this.rowOverrides[row] = null;
      }
    }
    this.notify();
    return assigned;
  }

  async decodePending(): Promise<void> {
    if (!this.context) return;
    for (const sample of this.samples) {
      if (sample.buffer || !sample.url) continue;
      try {
        const response = await fetch(sample.url);
        const data = await response.arrayBuffer();
        sample.buffer = await this.context.decodeAudioData(data.slice(0));
        // Only normalize user-loaded content — procedural sounds are already
        // tuned to a target loudness and don't need it.
        if (sample.source !== 'procedural' && sample.buffer) {
          SampleLibrary.normalizeBuffer(sample.buffer);
        }
      } catch (error) {
        console.warn(`Failed to decode sample "${sample.name}"`, error);
      }
    }
    this.notify();
  }

  /** In-place peak normalization. Scans the buffer, computes the loudest
   *  sample across all channels, and multiplies everything by `target/peak`
   *  so the loudest point lands at `target`. Skips near-silent buffers
   *  (peak < 0.02) to avoid amplifying noise floor, and buffers that are
   *  already loud (peak >= 0.85) so we don't undo intentional headroom. */
  static normalizeBuffer(buffer: AudioBuffer, target = 0.95): void {
    let peak = 0;
    for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
      const data = buffer.getChannelData(ch);
      for (let i = 0; i < data.length; i++) {
        const v = Math.abs(data[i]!);
        if (v > peak) peak = v;
      }
    }
    if (peak < 0.02 || peak >= 0.85) return;
    const gain = target / peak;
    for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
      const data = buffer.getChannelData(ch);
      for (let i = 0; i < data.length; i++) data[i]! *= gain;
    }
  }

  private synthesizeWav(
    frequency: number,
    duration: number,
    spec: SynthSpec,
    loop: boolean,
  ): ArrayBuffer {
    const sampleRate = 44100;
    const frames = Math.floor(sampleRate * duration);
    const data = new Float32Array(frames);
    const TWO_PI = Math.PI * 2;
    const centsToRatio = (cents: number) => Math.pow(2, cents / 1200);

    // Pre-compute per-preset constants.
    let unisonRatios: number[] | null = null;
    if (spec.kind === 'wt-supersaw') {
      const n = Math.max(2, Math.floor(spec.voices));
      unisonRatios = [];
      for (let v = 0; v < n; v++) {
        const c = (v - (n - 1) / 2) * spec.detune;
        unisonRatios.push(centsToRatio(c));
      }
    } else if (spec.kind === 'wt-reese') {
      unisonRatios = [centsToRatio(-spec.detune), centsToRatio(spec.detune)];
    } else if (spec.kind === 'wt-sub') {
      unisonRatios = [centsToRatio(-spec.detune), centsToRatio(spec.detune)];
    }
    // Formant table (very rough vowel resonances).
    const formants: Record<string, Array<{ f: number; a: number; bw: number }>> = {
      a: [{ f: 700, a: 1, bw: 90 }, { f: 1220, a: 0.6, bw: 110 }, { f: 2600, a: 0.35, bw: 180 }],
      e: [{ f: 400, a: 1, bw: 70 }, { f: 2100, a: 0.7, bw: 120 }, { f: 2900, a: 0.35, bw: 200 }],
      i: [{ f: 270, a: 1, bw: 60 }, { f: 2290, a: 0.75, bw: 130 }, { f: 3000, a: 0.4, bw: 210 }],
      o: [{ f: 450, a: 1, bw: 80 }, { f: 800, a: 0.55, bw: 100 }, { f: 2830, a: 0.3, bw: 190 }],
      u: [{ f: 325, a: 1, bw: 70 }, { f: 700, a: 0.5, bw: 90 }, { f: 2530, a: 0.25, bw: 200 }],
    };

    // Simple 2-pole resonator (for formant filter). One per formant, per channel-slot.
    type Reso = { f: number; a: number; bw: number; y1: number; y2: number; c1: number; c2: number };
    const resoBank: Reso[] = spec.kind === 'wt-formant'
      ? formants[spec.vowel]!.map((fm) => {
        const r = Math.exp((-Math.PI * fm.bw) / sampleRate);
        const c1 = 2 * r * Math.cos((2 * Math.PI * fm.f) / sampleRate);
        const c2 = -r * r;
        return { f: fm.f, a: fm.a, bw: fm.bw, y1: 0, y2: 0, c1, c2 };
      })
      : [];

    for (let i = 0; i < frames; i++) {
      const t = i / sampleRate;
      const phase = t * frequency * TWO_PI;
      let sample = 0;
      switch (spec.kind) {
        case 'sine':
          sample = Math.sin(phase);
          break;
        case 'triangle':
          sample = 2 * Math.abs(2 * ((t * frequency) % 1) - 1) - 1;
          break;
        case 'square':
          sample = Math.sin(phase) > 0 ? 0.5 : -0.5;
          break;
        case 'sawtooth':
          sample = 2 * ((t * frequency) % 1) - 1;
          break;
        case 'noise': {
          const noise = Math.random() * 2 - 1;
          const body = Math.sin(t * 180 * TWO_PI);
          sample = noise * 0.75 + body * 0.25;
          break;
        }
        case 'cowbell': {
          const a = Math.sin(t * 800 * TWO_PI) > 0 ? 0.5 : -0.5;
          const b = Math.sin(t * 540 * TWO_PI) > 0 ? 0.5 : -0.5;
          sample = a * 0.55 + b * 0.45;
          break;
        }
        case 'wt-sub': {
          // Saw + detuned partner + sub-octave sine.
          let sum = 0;
          for (const r of unisonRatios!) sum += 2 * ((t * frequency * r) % 1) - 1;
          sum /= unisonRatios!.length;
          const sub = Math.sin(t * frequency * 0.5 * TWO_PI) * 0.55;
          sample = sum * 0.7 + sub * 0.5;
          break;
        }
        case 'wt-reese': {
          let sum = 0;
          for (const r of unisonRatios!) sum += 2 * ((t * frequency * r) % 1) - 1;
          sample = sum / unisonRatios!.length;
          break;
        }
        case 'wt-supersaw': {
          let sum = 0;
          for (const r of unisonRatios!) sum += 2 * ((t * frequency * r) % 1) - 1;
          sample = (sum / unisonRatios!.length) * 0.9;
          break;
        }
        case 'wt-fm': {
          const mod = Math.sin(t * frequency * spec.ratio * TWO_PI) * spec.index;
          sample = Math.sin(phase + mod);
          break;
        }
        case 'wt-pwm': {
          const lfo = Math.sin(t * spec.lfoRate * TWO_PI) * spec.lfoDepth;
          const width = Math.max(0.05, Math.min(0.95, spec.width + lfo));
          const p = (t * frequency) % 1;
          sample = (p < width ? 1 : -1) * 0.6;
          break;
        }
        case 'wt-sync': {
          // Master rules the retrigger; slave saw runs at slaveMult.
          const masterPhase = (t * frequency) % 1;
          const slavePhase = (t * frequency * spec.slaveMult) % 1;
          // Reset slave whenever master wraps (approximate: use masterPhase to modulate).
          const wrap = masterPhase < 1 / frequency * sampleRate ? 0 : slavePhase;
          sample = 2 * wrap - 1;
          break;
        }
        case 'wt-additive': {
          let sum = 0;
          for (let h = 0; h < spec.harmonics.length; h++) {
            const amp = spec.harmonics[h] ?? 0;
            if (amp === 0) continue;
            sum += Math.sin(phase * (h + 1)) * amp;
          }
          sample = sum * 0.5;
          break;
        }
        case 'wt-bell': {
          // Ring-mod bell: carrier × modulator.
          const carrier = Math.sin(phase);
          const modulator = Math.sin(t * frequency * spec.modFreqMult * TWO_PI);
          sample = carrier * (1 - spec.modDepth + spec.modDepth * modulator);
          break;
        }
        case 'wt-formant': {
          // Sawtooth glottal source → resonators sum.
          const src = 2 * ((t * frequency) % 1) - 1;
          let out = 0;
          for (const r of resoBank) {
            const y = src + r.c1 * r.y1 + r.c2 * r.y2;
            r.y2 = r.y1;
            r.y1 = y;
            out += y * r.a * 0.15;
          }
          sample = out;
          break;
        }
      }

      // Envelope.
      let env = 1;
      if (loop) {
        const edge = 0.02;
        if (t < edge) env = t / edge;
        if (t > duration - edge) env = Math.max(0, (duration - t) / edge);
      } else if (spec.kind === 'noise') {
        env = Math.exp(-t * 22);
      } else if (spec.kind === 'cowbell') {
        env = Math.exp(-t * 14);
      } else {
        // Kick-territory shaping: percussive slots below 150 Hz get a tighter
        // body decay + a proper pitch drop + a short click transient so they
        // read as a punchy hit instead of a slow woody bump. Applied to
        // sine/triangle/wt-sub — the three preset kinds the kick slot rolls.
        const isKickBody = frequency < 150 && (
          spec.kind === 'sine' || spec.kind === 'triangle' || spec.kind === 'wt-sub'
        );
        const decayRate = isKickBody ? 18 : (frequency > 1000 ? 28 : 8);
        env = Math.exp(-t * decayRate);
        if (isKickBody) {
          // Pitch drop: fundamental sweeps down over the first ~50ms — the
          // signature "thump" of a kick. Overrides the raw osc so the drop
          // owns the body; a small trace of the original waveform keeps
          // wt-sub's detune audible.
          const drop = Math.sin(phase * Math.exp(-t * 14));
          sample = drop * 0.9 + sample * 0.15;
          // Click transient: sharp decaying noise burst layered on top for
          // the first ~4ms — reads as the initial batter-head impact before
          // the pitched body arrives. Gives back the fast attack that a
          // pure sine drop otherwise smooths out.
          if (t < 0.004) {
            const click = (Math.random() * 2 - 1) * Math.exp(-t * 500);
            sample = sample * 0.65 + click * 0.9;
          }
        }
      }
      data[i] = sample * env * 0.55;
    }

    // Level-match: scale every procedural buffer toward the same perceived loudness
    // so switching between kick, supersaw, FM bass etc. doesn't feel like a mixer
    // jump. Measure RMS on the sustained portion (skip the first 8ms of attack) and
    // scale toward a target RMS, then soft-limit peaks to 0.9.
    const attackSkip = Math.min(Math.floor(sampleRate * 0.008), frames);
    let sumSq = 0;
    let count = 0;
    let peak = 0;
    for (let i = attackSkip; i < frames; i++) {
      const s = data[i] ?? 0;
      sumSq += s * s;
      count++;
      const a = Math.abs(s);
      if (a > peak) peak = a;
    }
    const rms = count > 0 ? Math.sqrt(sumSq / count) : 0;
    const targetRms = 0.18;
    let gain = rms > 0.001 ? targetRms / rms : 1;
    gain = Math.max(0.25, Math.min(4, gain));
    // Peak safety: if applying gain would slam past 0.95, back the gain off.
    const projectedPeak = peak * gain;
    if (projectedPeak > 0.95) gain *= 0.95 / projectedPeak;
    for (let i = 0; i < frames; i++) {
      let v = (data[i] ?? 0) * gain;
      // Soft tanh limiter as the last kiss — inaudible until peaks approach 0.9.
      if (v > 0.85 || v < -0.85) v = Math.tanh(v * 1.1) * 0.9;
      data[i] = v;
    }

    return this.encodeWav(data, sampleRate);
  }

  private encodeWav(samples: Float32Array, sampleRate: number): ArrayBuffer {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);

    const writeString = (offset: number, value: string) => {
      for (let i = 0; i < value.length; i++) view.setUint8(offset + i, value.charCodeAt(i));
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + samples.length * 2, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, samples.length * 2, true);

    let offset = 44;
    for (let i = 0; i < samples.length; i++) {
      const s = Math.max(-1, Math.min(1, samples[i] ?? 0));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      offset += 2;
    }
    return buffer;
  }
}
